import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { SCORE_COLORS, SCORE_TIER_LABELS, parseLocalDate } from "./shared";
import ThemedChartTooltip, { ThemedTooltipBox } from "../shared/ThemedChartTooltip";
import { SkeletonChart } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

// "Jun 15" from a Date. Kept separate from the ISO-string entry point below so
// each has one unambiguous input type.
const formatDateLabel = (date) =>
  date ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

// "Jun 15" from a plain "YYYY-MM-DD" string. Parsed via parseLocalDate — passing
// the string to `new Date()` yields midnight UTC and renders a day early west of
// UTC (see the contract note in ./shared/format.js). Also the Recharts
// tickFormatter, which receives the raw `week_of` string.
const formatWeekLabel = (iso) => formatDateLabel(parseLocalDate(iso));

// Monday-of-week key (YYYY-MM-DD) for an event start_date, matching the backend's
// `week_of` values (which are Mondays). Uses the shared parseLocalDate (this file
// previously inlined the same part-wise parsing) to avoid the UTC day shift.
const weekKeyForDate = (startDate) => {
  const dt = parseLocalDate(startDate);
  if (!dt) return null;
  // JS getDay(): 0=Sun..6=Sat. Shift back to the Monday on/before this date.
  const offset = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - offset);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
};

// Build { week_of -> [event names] }, sorted by opportunity score desc so the
// tooltip leads with the most marketable events in that week.
const buildWeekEventNames = (events) => {
  const map = new Map();
  (events || []).forEach((eventItem) => {
    const key = weekKeyForDate(eventItem.start_date);
    if (!key || !eventItem.event_name) return;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ name: eventItem.event_name, score: Number(eventItem.opportunity_score || 0) });
  });
  map.forEach((list) => list.sort((eventA, eventB) => eventB.score - eventA.score));
  return map;
};

const formatWeekRange = (iso, rangeEnd) => {
  const start = parseLocalDate(iso);
  if (!start) return "";
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  if (rangeEnd) {
    const clamp = parseLocalDate(rangeEnd);
    if (clamp && clamp < end) {
      return `${formatDateLabel(start)} – ${formatDateLabel(clamp)}`;
    }
  }
  return `${formatDateLabel(start)} – ${formatDateLabel(end)}`;
};

const MAX_TOOLTIP_EVENTS = 3;

const TooltipContent = ({ active, payload, label, rangeEnd, eventsByWeek }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const total = Number(row.total || 0);
  const peak = Number(row.peak_score || 0);
  const isEmpty = total === 0;
  const names = eventsByWeek?.get(row.week_of) || [];
  const shown = names.slice(0, MAX_TOOLTIP_EVENTS);
  const remaining = names.length - shown.length;
  return (
    <ThemedTooltipBox title={`Week of ${formatWeekRange(label, rangeEnd)}`}>
      {isEmpty ? (
        <div style={{ opacity: 0.65, fontStyle: "italic" }}>No events recorded</div>
      ) : (
        <>
          <div style={{ opacity: 0.75, marginBottom: 6 }}>
            {total} Event{total === 1 ? "" : "s"}
            {peak > 0 && <> · {peak} Peak Score</>}
          </div>
          {[
            { key: "campaign_tier", tier: "campaign" },
            { key: "quickwin_tier", tier: "quickwin" },
            { key: "monitor_tier", tier: "monitor" },
            { key: "low_tier", tier: "low" },
          ].map(({ key, tier }) => {
            const count = Number(row[key] || 0);
            if (!count) return null;
            return (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SCORE_COLORS[tier] }} />
                <span>{SCORE_TIER_LABELS[tier]}</span>
                <span style={{ marginLeft: "auto", fontWeight: 600 }}>{count}</span>
              </div>
            );
          })}
          {shown.length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid rgba(148,163,184,0.25)" }}>
              <div style={{ opacity: 0.6, marginBottom: 3 }}>Events:</div>
              {shown.map((eventItem, index) => (
                <div key={index} style={{ display: "flex", alignItems: "baseline", gap: 8, maxWidth: 240 }}>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {eventItem.name}
                  </span>
                  <span style={{ opacity: 0.6, fontWeight: 600 }}>{eventItem.score}</span>
                </div>
              ))}
              {remaining > 0 && (
                <div style={{ opacity: 0.55, marginTop: 2 }}>+{remaining} more</div>
              )}
            </div>
          )}
        </>
      )}
    </ThemedTooltipBox>
  );
};

const WeeklyDensityPanel = ({ loading, error, weeks, events, rangeEnd }) => {
  const eventsByWeek = useMemo(() => buildWeekEventNames(events), [events]);

  const data = useMemo(() => {
    return (weeks || []).map((week) => ({
      ...week,
      campaign_tier: Number(week.campaign_tier || 0),
      quickwin_tier: Number(week.quickwin_tier || 0),
      monitor_tier: Number(week.monitor_tier || 0),
      low_tier: Number(week.low_tier || 0),
      total: Number(week.total || 0),
      peak_score: Number(week.peak_score || 0),
    }));
  }, [weeks]);

  const hasData = data.length > 0 && data.some((datum) => datum.total > 0);

  const tierBars = [
    { key: "campaign_tier", tier: "campaign" },
    { key: "quickwin_tier", tier: "quickwin" },
    { key: "monitor_tier", tier: "monitor" },
    { key: "low_tier", tier: "low" },
  ].filter(({ key }) => data.some((datum) => Number(datum[key] || 0) > 0));

  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Weekly Event Density</h3>
            <InfoTip text="Bars stack by score tier — high / mid / low. A tall tower of mostly-low events is different demand than a short tower of high-score events; read the colors, not just the height." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Upcoming events per week, stacked by opportunity score tier.
          </p>
        </div>
      </div>

      {loading ? (
        <SkeletonChart height={280} />
      ) : error ? (
        <ErrorState message={error} />
      ) : !hasData ? (
        <div className="text-center py-16 text-sm text-gray-500 dark:text-gray-400">
          No weekly event data available yet.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" vertical={false} />
            <XAxis
              dataKey="week_of"
              tickFormatter={formatWeekLabel}
              tick={{ fontSize: 11, fill: "currentColor" }}
              className="text-gray-500 dark:text-gray-400"
              axisLine={{ stroke: "currentColor" }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "currentColor" }}
              className="text-gray-500 dark:text-gray-400"
              axisLine={false}
              tickLine={false}
            />
            <ThemedChartTooltip content={<TooltipContent rangeEnd={rangeEnd} eventsByWeek={eventsByWeek} />} cursor={{ fill: "rgba(148, 163, 184, 0.1)" }} />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              iconType="square"
              iconSize={10}
              formatter={(value) => <span className="text-gray-600 dark:text-gray-300">{value}</span>}
            />
            {tierBars.map(({ key, tier }, index) => (
              <Bar
                key={key}
                stackId="tiers"
                dataKey={key}
                name={SCORE_TIER_LABELS[tier]}
                fill={SCORE_COLORS[tier]}
                radius={index === tierBars.length - 1 ? [4, 4, 0, 0] : 0}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default WeeklyDensityPanel;
